import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import api from "@/lib/axios"
import FormHeader from "@/components/FormHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useState } from "react"
import { FaPlus } from "react-icons/fa6"
import { Loader2 } from "lucide-react"
import { JalurJenis, LayananSmartPayment } from "@/types"
import { MdModeEdit } from "react-icons/md"
import { RiDeleteBack2Line } from "react-icons/ri"
import toast from "react-hot-toast"
import { AxiosError } from "axios"
import { getListJalurJenis } from "@/services/getData"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

const postData = async (newData: LayananSmartPayment) => {
  const response = await api.post("/master/layanan-smartpayment/store", newData)
  return response.data
}

const patchData = async (newData: LayananSmartPayment) => {
  const response = await api.patch(
    "/master/layanan-smartpayment/update",
    newData
  )
  return response.data
}

const deleteData = async (deletedData: {
  id_ref_layanan_smartpayment: string
}) => {
  const response = await api({
    url: "/master/layanan-smartpayment/delete",
    method: "DELETE",
    data: deletedData,
  })
  return response.data
}

export default function Form({
  method,
  selectedRow,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE"
  selectedRow?: LayananSmartPayment | null
  closeModal: () => void
}) {
  const [form, setForm] = useState<LayananSmartPayment>({
    id_ref_layanan_smartpayment: selectedRow?.id_ref_layanan_smartpayment || "",
    id_ref_jalur_jenis: selectedRow?.id_ref_jalur_jenis || "",
    id_unit_smartpayment: `${selectedRow?.id_unit_smartpayment}` || "",
    jenis: selectedRow?.jenis || "",
    kode_layanan_smartpayment: selectedRow?.kode_layanan_smartpayment || "",
    nama_layanan_smartpayement: selectedRow?.nama_layanan_smartpayement || "",
  })

  const isPost = method === "POST"
  const isPatch = method === "PATCH"
  const isDelete = method === "DELETE"

  const { data: dataJalurJenis } = useQuery({
    queryKey: ["listJalurJenis"],
    queryFn: getListJalurJenis,
  })

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listLayananSmartPayment"] })
      closeModal()
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mutate(form)
  }

  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Delete Data Layanan smart payment"
          description={`Apakah anda yakin akan menghapus data "${selectedRow?.nama_layanan_smartpayement}"?`}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-3"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Hapus"
            )}
          </Button>
        </div>
      </form>
    )

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={
          isPost
            ? "Insert Data Layanan Smartpayment"
            : "Update Data Layanan Smartpayment"
        }
      />

      <div>
        <Label>Jalur Jenis</Label>
        <Select
          value={form.id_ref_jalur_jenis}
          onValueChange={(value) =>
            setForm((prev) => ({
              ...prev,
              id_ref_jalur_jenis: value,
            }))
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Pilih Jalur Jenis">
              {form.id_ref_jalur_jenis &&
                dataJalurJenis?.find(
                  (jalurJenis: JalurJenis) =>
                    jalurJenis.id_ref_jalur_jenis === form.id_ref_jalur_jenis
                )?.nama_jalur_jenis}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {dataJalurJenis?.map((jalurJenis: JalurJenis, index: number) => (
              <SelectItem
                key={index}
                value={`${jalurJenis.id_ref_jalur_jenis}`}
              >
                {jalurJenis?.nama_jalur_jenis}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Jenis</Label>
        <Select
          value={form.jenis}
          onValueChange={(value) =>
            setForm((prev) => ({
              ...prev,
              jenis: value as "daftar" | "ukt",
            }))
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Pilih Jenis">
              {form.jenis &&
                ["daftar", "ukt"]?.find((value) => value === form.jenis)}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {["daftar", "ukt"]?.map((value: string, index: number) => (
              <SelectItem key={index} value={`${value}`}>
                {value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Id Unit Smartpayment</Label>
        <Input
          placeholder="Ketik kode id unit smartpayment..."
          value={form.id_unit_smartpayment}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              id_unit_smartpayment: event.target.value,
            }))
          }
        />
      </div>

      <div>
        <Label>Kode Layanan Smartpayment</Label>
        <Input
          maxLength={2}
          placeholder="Ketik kode layanan smartpayment..."
          value={form.kode_layanan_smartpayment}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              kode_layanan_smartpayment: event.target.value,
            }))
          }
        />
      </div>

      <div>
        <Label>Nama Layanan Smartpayment</Label>
        <Input
          placeholder="Ketik nama layanan smartpayment..."
          value={form.nama_layanan_smartpayement}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              nama_layanan_smartpayement: event.target.value,
            }))
          }
        />
      </div>

      <Button className="mt-3 flex items-center gap-3" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        ) : (
          "Simpan"
        )}
      </Button>
    </form>
  )
}
